import { useEffect, useRef, useState } from "react";
import XCircleIcon from "@heroicons/react/20/solid/XCircleIcon";
import { useMutation } from "@tanstack/react-query";
import Balance from "react-wrap-balancer";
import { z } from "zod";

import { useBreakpointContext } from "@hsl/core/hooks/useBreakpoint";
import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";

import useTranslation, { Translate } from "../../../hooks/useTranslation";
import { UserDetailsService } from "../../../openapi";
import Button from "../../Button";
import EnvelopeCheckIcon from "../../icons/EnvelopeCheck";
import EnvelopeOpen from "../../icons/EnvelopeOpen";
import Spinner from "../../Spinner";
import { FloatingBox } from "../FloatingBox";
import { txStrReplacer as replacer } from "../lib";
import { DetailFormInput } from "../SubscriptionFlows/Steps/AddDetails/FormComponents";

export const RetrieveFunds = () => {
    const { clickLinkEvent } = useYardActionEvents();
    const currentBreakpoint = useBreakpointContext()?.currentBreakpoint;
    const isXS = currentBreakpoint === "xs";
    const tx = useTranslation();
    const subscribeTxt = tx(
        "Subscribe to funds by clicking on the bell [^] icons to receive emails when fact sheets and other fund literature is published or updated",
    ).result;
    const watchTxt = tx(
        "Create your own watchlist of funds by clicking on the star [*] icons",
    ).result;
    const iconClasses = "inline h-4 w-4 align-sub";
    const isRetrievingFunds = useFCSubscriptionStore(
        (store) => store.retrieveFunds,
    );
    const setIsRetrievingFunds = useFCSubscriptionStore(
        (store) => store.setRetrieveFunds,
    );
    const [step, setStep] = useState<"initial" | "form" | "sent">("initial");
    const [email, setEmail] = useState("");
    const ref = useRef<HTMLInputElement>(null!);
    const [resent, setResent] = useState(false);
    const triggerRetrieveEvent = (
        type: "retrieve" | "retrieve_form" | "resend" | "change",
        email?: string,
    ) => {
        clickLinkEvent({
            name:
                type === "retrieve"
                    ? "Retrieve your funds"
                    : type === "retrieve_form"
                      ? "Retrieve"
                      : type === "resend"
                        ? "Resend email"
                        : "Change email",
            position: "Tooltip",
            email,
        });
    };

    const retrieve = useMutation({
        mutationFn: async (email: string) => {
            if (!email) throw new Error("Please enter email address");
            if (!z.string().email().safeParse(email).success)
                throw new Error("Please enter a valid email address");

            try {
                await UserDetailsService.resendUserEmail({ email });
            } catch (err) {
                console.error(err);
                throw new Error(
                    "Couldn't connect to LGIM Fund Collateral Subscriptions Service database. Please try again later.",
                );
            }
        },
        onMutate: () => {
            setResent(false);
        },
        onSuccess: () => {
            if (step === "sent") {
                setResent(true);
            } else {
                setStep("sent");
            }
        },
    });

    const user = useFCSubscriptionStore((store) => store.user);

    useEffect(() => {
        if (!isRetrievingFunds) {
            setStep("initial");
            setEmail("");
        }
    }, [isRetrievingFunds]);

    useEffect(() => {
        if (step === "initial") {
            // close box if click outside if on first step
            const handleClickOutside = (e: MouseEvent) => {
                if (ref.current && !ref.current.contains(e.target as Node)) {
                    setIsRetrievingFunds(false);
                }
            };
            document.addEventListener("mousedown", handleClickOutside);
            return () =>
                document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [step, setIsRetrievingFunds]);

    let content = null;

    function handleRetrieveClick() {
        setStep("form");
        triggerRetrieveEvent("retrieve");
    }

    switch (step) {
        case "initial":
            content = (
                <div className="flex flex-col py-2">
                    <p className="my-2">
                        {replacer(subscribeTxt, { iconClasses })}.{" "}
                    </p>
                    <p className="my-2">
                        {replacer(watchTxt, { iconClasses })}.
                    </p>
                    {!user && (
                        <>
                            <hr className="my-2 w-full" />
                            <p className="my-2">
                                <Translate>
                                    If you previously set up a subscription or
                                    watchlist, please retrieve your funds by
                                    clicking on the button below
                                </Translate>
                                .
                            </p>
                            <Button
                                variant="large"
                                className="mb-4 mt-2 self-center"
                                onClick={handleRetrieveClick}
                            >
                                <Translate>Retrieve your funds</Translate>
                            </Button>
                        </>
                    )}
                </div>
            );
            break;
        case "form":
            content = (
                <form
                    className="flex flex-col py-4"
                    onSubmit={(evt) => {
                        evt.preventDefault();
                        triggerRetrieveEvent("retrieve_form", email.trim());
                        retrieve.mutate(email.trim());
                    }}
                >
                    <p>
                        <Translate>
                            Please provide your email address. If you have
                            subscriptions or a watchlist, we will then send you
                            an email with a verification link
                        </Translate>
                        .
                    </p>
                    <div className="mx-auto my-4">
                        <DetailFormInput icon={<EnvelopeOpen />}>
                            <input
                                type="email"
                                autoComplete="email"
                                placeholder={tx("Email").result}
                                disabled={retrieve.isLoading}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={cx(
                                    retrieve.isError &&
                                        "border-core-red focus:border-taupe-grey",
                                )}
                            />
                        </DetailFormInput>
                        {retrieve.isError && (
                            <div className="text-core-red mt-4 flex items-start gap-2 pl-12">
                                <XCircleIcon
                                    width={20}
                                    className="mt-0.5 shrink-0"
                                />
                                <p>
                                    <Translate>
                                        {retrieve.error instanceof Error
                                            ? retrieve.error.message
                                            : "Something went wrong, please check your email address and try again"}
                                    </Translate>
                                </p>
                            </div>
                        )}
                    </div>
                    <Button
                        variant="large"
                        className="my-2 self-center"
                        type="submit"
                        disabled={retrieve.isLoading}
                    >
                        <Translate>Retrieve your funds</Translate>
                    </Button>
                </form>
            );
            break;
        case "sent":
            content = (
                <div className="flex flex-col items-center py-4 text-center">
                    <EnvelopeCheckIcon className="w-16 shrink-0" />
                    <Balance as="h2" className="my-2 max-w-[80%] font-medium">
                        <Translate>
                            Please go to your inbox to verify your email
                        </Translate>
                    </Balance>
                    <p className="my-1.5 text-sm">
                        <Translate>Email address</Translate>:{" "}
                        <span className="pii font-medium">{email}</span>
                    </p>
                    <button
                        type="button"
                        className="text-core-blue my-1 text-sm underline"
                        onClick={() => {
                            retrieve.mutate(email);
                            triggerRetrieveEvent("resend");
                        }}
                    >
                        {retrieve.isLoading ? (
                            <Spinner className="h-3 w-3" />
                        ) : resent ? (
                            <>
                                <Translate>Sent</Translate>!
                            </>
                        ) : (
                            <Translate>Re-send email</Translate>
                        )}
                    </button>
                    <button
                        type="button"
                        className="text-core-blue my-1 text-sm underline"
                        onClick={() => {
                            setStep("form");
                            triggerRetrieveEvent("change");
                        }}
                    >
                        <Translate>Change email address</Translate>
                    </button>
                </div>
            );
            break;
    }

    return (
        <FloatingBox
            key={isXS ? "xs" : "lg"}
            boxRef={ref}
            show={isRetrievingFunds}
            onClose={() => setIsRetrievingFunds(false)}
            backdrop={isXS}
            header={
                <h2 className="ml-2 text-lg font-medium">
                    {step === "initial" ? (
                        <Translate>Subscriptions and watchlists</Translate>
                    ) : (
                        <Translate>
                            Retrieve subscriptions and watchlists
                        </Translate>
                    )}
                </h2>
            }
        >
            {content}
        </FloatingBox>
    );
};
